import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_ImageDenoiser = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/image-denoiser',
			name: 'AI Image Denoiser – Remove Noise from Photos',
			description:
				"Lift's AI Image Denoiser cleans noise and grain from low-light or high-ISO photos while preserving natural texture and detail. Restore clarity in seconds",
			url: 'https://liftapp.ai/image-denoiser',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/image-denoiser#product' },
				{ '@id': 'https://liftapp.ai/image-denoiser#howto' },
				{ '@id': 'https://liftapp.ai/image-denoiser#benefits' },
				{ '@id': 'https://liftapp.ai/image-denoiser#faq' },
				{ '@id': 'https://liftapp.ai/image-denoiser#why-lift' },
				{ '@id': 'https://liftapp.ai/image-denoiser#explore-tools' },
				{ '@id': 'https://liftapp.ai/image-denoiser#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/image-denoiser#product',
			name: 'AI Image Denoiser',
			description:
				"Lift's AI Image Denoiser removes grain, noise, and compression artifacts from your photos automatically. Get crystal-clear, natural-looking images with one click.",
			url: 'https://liftapp.ai/image-denoiser',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/image-denoiser#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: 'Choose a photo from your device or take one using Lift’s app',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let AI clean noise',
					text: 'Lift’s AI denoiser detects and removes noise while keeping fine texture intact.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-denoiser#benefits',
			name: 'Who can benefit from AI Image Denoiser?',
			description:
				'Lift AI Image Denoiser helps e-commerce sellers, content creators, marketers, and more improve image quality instantly with AI retouching.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'AI Image Denoiser is essential for anyone dealing with grainy or noisy photographs',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Content creators and influencers for professional-looking posts',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Businesses and e-commerce sellers improving product visuals',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Event planners preserving memories in crisp detail',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students or professionals can clarify scanned documents, screenshots, or images',
				},
				{
					'@type': 'ListItem',
					position: 6,
					name: 'Photographers can restore older or low-quality images effortlessly',
				},
				{
					'@type': 'ListItem',
					position: 7,
					name: 'AI Image Denoiser delivers professional noise reduction in seconds, making it indispensable for photographers and content creators at every level',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/Landing_2.8f6a6794.webp',
				name: 'Benefit from Image Denoiser',
				description:
					'Blurry shot of two people walking becomes crisp and vivid near orange wall',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/image-denoiser#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Image Denoiser?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Image Denoiser is a premium online tool that improves photo clarity, colour and resolution up to 4K in a single click.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Image Denoiser work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'AI denoise photo quality uses a two-step deep-learning process: first, a smart denoiser cleans blur and noise; next, a super-resolution model rebuilds lost pixels, boosts colours and sharpens edges all the way up to crisp 4K. Everything runs in the cloud, so you get pro-level photo quality with one click.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will denoising make my image blurry?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "No. Unlike basic smoothing filters, Lift's AI denoiser removes noise while actively preserving edges and fine details. The result is a clean image that stays sharp and natural-looking.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the result without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-denoiser#why-lift',
			name: "Why Lift's AI Image Denoiser Stands Out",
			description:
				"Lift's AI Image Denoiser provides professional results, AI accuracy, cross-platform support, and is budget-friendly.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Professional results',
					description:
						'Achieve studio-quality images without the need for complex tools',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI accuracy',
					description:
						'Smart algorithms deliver enhancements tailored to your specific image',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly editing services with Lift’s affordable solution',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/Landing_3.cf1e0ce2.webp',
				name: 'Denoised with Lift',
				description:
					'Noisy strawberry photo refined to rich red detail and clarity',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/image-extender#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/image-denoiser#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Image Denoiser',
					item: 'https://liftapp.ai/image-denoiser',
				},
			],
		},
	],
};
